import { useCallback, useEffect, useId, useReducer, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { formatScanDate, scanTakenAt, type DmScanSummary } from '@odo/shared-ui';
import { ModalShell } from '../../../../shared/ModalShell';
import { useEscapeKey } from '../../../../../hooks/useEscapeKey';
import type { AttachmentNotice } from '../composer/useComposerAttachment';
import { DiscardMarksConfirm } from './DiscardMarksConfirm';
import { INITIAL_MARKUP_STATE, hasMarks, markupReducer } from './markupHistory';
import { buildScanPhotoFileName } from './scanPhotoFileName';
import type { MarkupPhoto } from './scanPhotoOptions';
import { ScanMarkupEditor } from './ScanMarkupEditor';
import { ScanPhotoPicker } from './ScanPhotoPicker';

export interface ScanMarkupDialogProps {
    patientId: string;
    dmId: number | null;
    notice: AttachmentNotice | null;
    onAttach: (file: Blob, name: string) => boolean;
    onClose: () => void;
}

interface EditingPhoto {
    photo: MarkupPhoto;
    scanDate: string;
}

type LeaveTarget = 'photos' | 'composer';

const TITLE = 'Mark up a scan photo';
const PICKER_SUBTITLE =
    'Pick a photo from a Dental Monitoring scan, then draw on it to show the patient what you mean.';

function ScanMarkupDialog({ patientId, dmId, notice, onAttach, onClose }: ScanMarkupDialogProps) {
    const titleId = useId();
    const closeRef = useRef<HTMLButtonElement>(null);
    const [selectedScanId, setSelectedScanId] = useState<number | null>(null);
    const [editing, setEditing] = useState<EditingPhoto | null>(null);
    const [markup, dispatch] = useReducer(markupReducer, INITIAL_MARKUP_STATE);
    const [confirming, setConfirming] = useState<LeaveTarget | null>(null);

    useEffect(() => {
        closeRef.current?.focus();
    }, []);

    const leave = useCallback(
        (target: LeaveTarget) => {
            if (target === 'composer') {
                onClose();
                return;
            }
            setEditing(null);
            dispatch({ type: 'reset' });
        },
        [onClose]
    );

    const requestLeave = useCallback(
        (target: LeaveTarget) => {
            if (editing && hasMarks(markup)) setConfirming(target);
            else leave(target);
        },
        [editing, markup, leave]
    );

    useEscapeKey(() => requestLeave('composer'), confirming == null);

    const pick = (photo: MarkupPhoto, scan: DmScanSummary) => {
        dispatch({ type: 'reset' });
        setEditing({ photo, scanDate: scanTakenAt(scan) });
    };

    const attach = useCallback(
        (jpeg: Blob) => {
            if (!editing) return false;
            const attached = onAttach(jpeg, buildScanPhotoFileName(editing.scanDate));
            if (attached) onClose();
            return attached;
        },
        [editing, onAttach, onClose]
    );

    return createPortal(
        <>
            <ModalShell
                widthClassName="max-w-[1400px]"
                panelClassName="rounded-xl flex flex-col h-full max-h-[960px] overflow-hidden"
                dismissible={false}
                onDismiss={() => requestLeave('composer')}
                labelledBy={titleId}
            >
                <header
                    className={`flex justify-between gap-4 px-6 border-b border-gray-200 ${
                        editing ? 'items-center py-3' : 'items-start pt-5 pb-4'
                    }`}
                >
                    {editing ? (
                        <h2
                            id={titleId}
                            className="min-w-0 truncate text-lg font-bold text-gray-800"
                        >
                            {TITLE}
                            <span className="font-normal text-gray-500">
                                {` · ${editing.photo.label} · ${formatScanDate(editing.scanDate)}`}
                            </span>
                        </h2>
                    ) : (
                        <div className="min-w-0">
                            <h2 id={titleId} className="text-xl font-bold text-gray-800">
                                {TITLE}
                            </h2>
                            <p className="mt-0.5 text-sm text-gray-500 truncate">
                                {PICKER_SUBTITLE}
                            </p>
                        </div>
                    )}
                    <button
                        ref={closeRef}
                        type="button"
                        onClick={() => requestLeave('composer')}
                        aria-label="Close"
                        className="-mr-1 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </header>
                {editing ? (
                    <ScanMarkupEditor
                        photo={editing.photo}
                        markup={markup}
                        dispatch={dispatch}
                        notice={notice}
                        shortcutsEnabled={confirming == null}
                        onBack={() => requestLeave('photos')}
                        onAttach={attach}
                    />
                ) : (
                    <ScanPhotoPicker
                        patientId={patientId}
                        dmId={dmId}
                        selectedScanId={selectedScanId}
                        onSelectScan={setSelectedScanId}
                        onPick={pick}
                        onCancel={onClose}
                    />
                )}
            </ModalShell>
            {confirming && (
                <DiscardMarksConfirm
                    leavingTo={confirming}
                    onKeep={() => setConfirming(null)}
                    onDiscard={() => {
                        setConfirming(null);
                        leave(confirming);
                    }}
                />
            )}
        </>,
        document.body
    );
}

export { ScanMarkupDialog };
